import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import {
  Keyboard,
  LayoutChangeEvent,
  Platform,
  Pressable,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import VXLTextInput, {
  VXLTextInputProps,
} from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import theme from 'shared/styles/theme';

const MENU_OPTION_MIN_HEIGHT = 54;
const MENU_MAX_HEIGHT = MENU_OPTION_MIN_HEIGHT * 4.5;
const MENU_VERTICAL_OFFSET = theme.spacing.xs;
const MENU_OVERLAY_SPACE = MENU_MAX_HEIGHT + MENU_VERTICAL_OFFSET;

/**
 * Extra bottom scroll space a screen must reserve when a VXLDropdown could be
 * the last (or near-last) focusable field before the end of scrollable content.
 * See VXLDropdown.mdx "Avoiding menu clipping near the bottom of a screen".
 */
export const VXL_DROPDOWN_MENU_RESERVED_SPACE = MENU_OVERLAY_SPACE;

const getNextOptionIndex = ({
  activeOptionIndex,
  direction,
  optionCount,
}: {
  activeOptionIndex: number;
  direction: 1 | -1;
  optionCount: number;
}) => {
  if (activeOptionIndex === -1) {
    if (direction === 1) {
      return 0;
    }

    return optionCount - 1;
  }

  return Math.min(Math.max(activeOptionIndex + direction, 0), optionCount - 1);
};

export interface VXLDropdownProps extends Omit<
  VXLTextInputProps,
  | 'caretHidden'
  | 'leftIcon'
  | 'multiline'
  | 'onChangeText'
  | 'readOnly'
  | 'rightIcon'
  | 'showSoftInputOnFocus'
  | 'value'
> {
  /** Options shown in the contextual menu. */
  options: string[];
  /** Currently selected value. The dropdown is fully controlled. */
  value: string;
  /** Called when the user selects an option from the menu. */
  onChangeText: (value: string) => void;
}

function VXLDropdown({
  disabled,
  editable,
  labelText = '',
  onBlur,
  onChangeText,
  onFocus,
  onKeyPress,
  onPressIn,
  options,
  value,
  ...inputProps
}: VXLDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeOption, setActiveOption] = useState<string | null>(null);
  const [fieldHeight, setFieldHeight] = useState(0);
  const ignoreNextBlurRef = useRef(false);
  const inputRef = useRef<TextInput>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const menuId = `${useId()}-menu`;
  const isDisabled = disabled === true || editable === false;
  const hasOptions = options.length > 0;
  const selectedOptionIndex = options.findIndex((option) => option === value);
  const showMenu = isOpen && !isDisabled && hasOptions;
  const trimmedLabelText = labelText.trim();
  const openMenuAccessibilityLabel =
    trimmedLabelText.length > 0
      ? `Open ${trimmedLabelText} options`
      : 'Open options';
  const closeMenuAccessibilityLabel =
    trimmedLabelText.length > 0
      ? `Close ${trimmedLabelText} options`
      : 'Close options';

  const scrollOptionIntoView = (optionIndex: number) => {
    scrollViewRef.current?.scrollTo({
      animated: true,
      y: optionIndex * MENU_OPTION_MIN_HEIGHT,
    });
  };

  const handleFieldLayout = (event: LayoutChangeEvent) => {
    setFieldHeight(event.nativeEvent.layout.height);
  };

  const handleCloseMenu = () => {
    setIsOpen(false);
    setActiveOption(null);
    ignoreNextBlurRef.current = false;
  };

  const handleOpenMenu = () => {
    if (!isDisabled) {
      setIsOpen(true);
    }
  };

  const handleToggleMenu = () => {
    if (isDisabled) {
      return;
    }

    if (isOpen) {
      handleCloseMenu();
    } else {
      Keyboard.dismiss();
      handleOpenMenu();
    }
  };

  const handleSelectOption = (option: string) => {
    onChangeText(option);
    handleCloseMenu();
    inputRef.current?.blur();
  };

  const handleOptionPressIn = () => {
    if (Platform.OS === 'web') {
      return;
    }

    ignoreNextBlurRef.current = true;
  };

  const handleOptionPressOut = () => {
    if (Platform.OS === 'web') {
      return;
    }

    ignoreNextBlurRef.current = false;
  };

  const handleOptionPress = (option: string) => {
    handleSelectOption(option);
  };

  const handleChevronPress = () => {
    if (isDisabled) {
      return;
    }

    if (isOpen) {
      handleCloseMenu();
      inputRef.current?.blur();
      return;
    }

    Keyboard.dismiss();
    inputRef.current?.focus();
    handleOpenMenu();
  };

  const handleMoveActiveOption = (direction: 1 | -1) => {
    if (!hasOptions) {
      handleOpenMenu();
      return;
    }

    const activeOptionIndex = options.findIndex(
      (option) => option === activeOption,
    );
    const nextOptionIndex = getNextOptionIndex({
      activeOptionIndex,
      direction,
      optionCount: options.length,
    });
    const nextOption = options[nextOptionIndex];

    if (nextOption) {
      setActiveOption(nextOption);
      handleOpenMenu();
      scrollOptionIntoView(nextOptionIndex);
    }
  };

  useLayoutEffect(() => {
    if (showMenu && selectedOptionIndex >= 0) {
      scrollViewRef.current?.scrollTo({
        animated: false,
        y: selectedOptionIndex * MENU_OPTION_MIN_HEIGHT,
      });
    }
  }, [selectedOptionIndex, showMenu]);

  return (
    <View
      style={[styles.container, showMenu ? styles.containerOpen : null]}
      testID="vxl-dropdown-container"
    >
      <View onLayout={handleFieldLayout} testID="vxl-dropdown-input-anchor">
        <VXLTextInput
          {...inputProps}
          ref={inputRef}
          labelText={labelText}
          value={value}
          disabled={isDisabled}
          editable={isDisabled ? false : editable}
          onBlur={(event) => {
            if (ignoreNextBlurRef.current) {
              ignoreNextBlurRef.current = false;
              setIsOpen(true);
              inputRef.current?.focus();
              return;
            }

            handleCloseMenu();
            onBlur?.(event);
          }}
          onFocus={(event) => {
            handleOpenMenu();
            onFocus?.(event);
          }}
          onKeyPress={(event) => {
            const { key } = event.nativeEvent;

            if (key === 'ArrowDown') {
              handleMoveActiveOption(1);
              return;
            }

            if (key === 'ArrowUp') {
              handleMoveActiveOption(-1);
              return;
            }

            if (key === 'Enter' && activeOption) {
              handleSelectOption(activeOption);
              return;
            }

            if (key === 'Escape') {
              handleCloseMenu();
              return;
            }

            onKeyPress?.(event);
          }}
          onPressIn={(event) => {
            onPressIn?.(event);
            handleToggleMenu();
          }}
          showSoftInputOnFocus={false}
          caretHidden
          readOnly
          rightIcon={{
            iconKey: showMenu ? 'CHEVRON_UP' : 'CHEVRON_DOWN',
            iconAccessibilityLabel: showMenu
              ? closeMenuAccessibilityLabel
              : openMenuAccessibilityLabel,
            onPress: handleChevronPress,
          }}
          aria-expanded={showMenu}
          aria-controls={showMenu ? menuId : undefined}
          role="combobox"
        />
      </View>
      {showMenu ? (
        <View
          style={[
            styles.menuAnchor,
            { top: fieldHeight + MENU_VERTICAL_OFFSET },
          ]}
          testID="vxl-dropdown-menu-surface"
          {...(Platform.OS === 'web' && {
            onMouseDown: (event: { preventDefault: () => void }) =>
              event.preventDefault(),
          })}
        >
          <ScrollView
            ref={scrollViewRef}
            nativeID={menuId}
            style={styles.menu}
            contentContainerStyle={styles.menuContent}
            keyboardShouldPersistTaps="always"
            nestedScrollEnabled
            showsVerticalScrollIndicator={hasOptions}
          >
            {options.map((option) => {
              const isSelected = option === value;
              const isActive = option === activeOption;

              return (
                <Pressable
                  key={option}
                  role="option"
                  accessibilityLabel={option}
                  accessibilityState={{ selected: isSelected }}
                  aria-selected={isSelected}
                  onPress={() => handleOptionPress(option)}
                  onPressIn={handleOptionPressIn}
                  onPressOut={handleOptionPressOut}
                  {...(Platform.OS === 'web' && {
                    onMouseDown: (event: { preventDefault: () => void }) =>
                      event.preventDefault(),
                  })}
                  style={({ pressed, hovered }) => [
                    styles.optionRow,
                    !pressed && (hovered || isActive)
                      ? styles.optionRowHovered
                      : null,
                    pressed ? styles.optionRowPressed : null,
                  ]}
                >
                  {({ pressed, hovered }) => {
                    const useInverseText = pressed || hovered || isActive;

                    return (
                      <>
                        {isSelected ? (
                          <Icon
                            iconKey="LIST_CHECK_MARK"
                            iconSize="MD"
                            iconColor={
                              useInverseText
                                ? theme.color.text.inverse
                                : theme.color.text.primary
                            }
                          />
                        ) : null}
                        <BodyBaseText
                          numberOfLines={1}
                          ellipsizeMode="tail"
                          style={[
                            styles.optionText,
                            useInverseText ? styles.optionTextInverse : null,
                          ]}
                        >
                          {option}
                        </BodyBaseText>
                      </>
                    );
                  }}
                </Pressable>
              );
            })}
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 1,
  },
  containerOpen: {
    elevation: 8,
    marginBottom: -MENU_OVERLAY_SPACE,
    paddingBottom: MENU_OVERLAY_SPACE,
    zIndex: 1000,
  },
  menu: {
    backgroundColor: theme.color.background.primary,
    borderColor: theme.color.border.primarySolid,
    borderRadius: 4,
    borderWidth: 1,
    boxShadow:
      '0 4px 36px 0 rgba(0, 0, 0, 0.16), 0 2px 8px 0 rgba(0, 0, 0, 0.12)',
    elevation: 8,
    maxHeight: MENU_MAX_HEIGHT,
    overflow: 'hidden',
  },
  menuContent: {
    backgroundColor: theme.color.background.primary,
  },
  menuAnchor: {
    backgroundColor: theme.color.background.primary,
    elevation: 8,
    left: 0,
    position: 'absolute',
    right: 0,
    zIndex: 1000,
  },
  optionRow: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderBottomColor: theme.color.border.primarySolid,
    borderBottomWidth: 1,
    flexDirection: 'row',
    gap: theme.spacing.m,
    minHeight: MENU_OPTION_MIN_HEIGHT,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  optionRowHovered: {
    backgroundColor: theme.color.interactive.primaryHovered,
  },
  optionRowPressed: {
    backgroundColor: theme.color.interactive.primaryPressed,
  },
  optionText: {
    color: theme.color.text.primary,
    flex: 1,
  },
  optionTextInverse: {
    color: theme.color.text.inverse,
  },
});

export default VXLDropdown;
